import {
  Card,
  Table,
  Space,
  Tag,
  Button,
  Modal,
  DatePicker,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tooltip,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { saveApi, listApi, deleteApi, restartApi } from "../../api/appt-list";
import moment from "moment";
import dayjs from "dayjs";

const utc = require("dayjs/plugin/utc"); // 引入utc插件
dayjs.extend(utc); // 使用utc插件

const { RangePicker } = DatePicker;

// 码头标题的颜色
const terminalColors = {
  lbct: "#0074E4",
  apm: "#FF6F61",
  pct: "#00A19D",
  pera: "#FFAC00",
  its: "#6B4226",
  tti: "#C2BB6A",
  trapac: "#3B3B3B",
  yti: "#5DA271",
  wbct: "#FF784E",
  ets: "#52658F",
  fms: "#D4A5A5",
};

export default function ApptListPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [total, setTotal] = useState(0);
  const [container, setContainer] = useState(null);
  const editRef = useRef({});

  //显示分页
  const pageRef = useRef(1);
  const sizeRef = useRef(40);

  useEffect(() => {
    getListData();
  }, []);

  //获取账号列表
  const getListData = async (
    page = pageRef.current,
    pageSize = sizeRef.current
  ) => {
    const result = await listApi({ page, size: pageSize, container });
    pageRef.current = page;
    sizeRef.current = pageSize;
    console.log("result", result);
    setDataList(result.list);
    setTotal(result.total);
  };

  //重启任务操作
  const restartSave = async (id, status) => {
    await restartApi({ id, status });
    message.success("Success");
    getListData();
  };

  //保存码头账号
  const onFinish = async (values) => {
    // console.log("Success:", values);
    const startTime = dayjs(values["timeSlot"][0])
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endTime = dayjs(values["timeSlot"][1])
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    // console.log("startTime", startTime, endTime);

    // check which terminal / type is supported by new scraper and use selector if so
    const newScraperSupportedTerminals = {
      "ets": {
        0: true
      },
      "apm": {
        0: true,
        1: true,
      },
      "lbct": {
        0: true,
        1: true,
      }
    }
    if (!!newScraperSupportedTerminals[values.terminal] && newScraperSupportedTerminals[values.terminal][values.type]) {
      values.scraperSelector = 1
    }

    if (!!values.container) {
      values.container = values.container.trim()
    }

    await saveApi({ ...values, startTime, endTime, status: 0 });
    handleCancel();
    message.success("Success");
    getListData();
  };

  //删除操作
  const handleDelte = async (record) => {
    await deleteApi(record["id"]);
    message.success("Success");
    getListData();
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  function calculateTimeDifference(updateTime) {
    // 获取当前时间
    const now = moment();
    // 将 updateTime 转换为本地时间
    const updateTimeMoment = moment.utc(updateTime).local();
    // 计算时间差
    const duration = moment.duration(now.diff(updateTimeMoment));
    // 获取分钟数和秒数
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    return { minutes, seconds };
  }

  const columns = [
    {
      title: "TERMINAL",
      dataIndex: "terminal",
      key: "terminal",
      align: "center",
      render: (text) => (
        <a style={{ color: terminalColors[text] }}>{text.toUpperCase()}</a>
      ),
    },
    {
      title: "CONTAINER",
      dataIndex: "container",
      key: "container",
    },
    {
      title: "SL #",
      dataIndex: "shippingLine",
      key: "shippingLine",
      width: "80px",
    },
    {
      title: "ISO #",
      dataIndex: "isoNumber",
      key: "isoNumber",
      width: "80px",
    },
    {
      title: "PIN #",
      dataIndex: "pin",
      key: "pin",
      width: "80px",
    },
    {
      title: "TYPE #",
      key: "type",
      dataIndex: "type",
      render: (_, { type }) => {
        const statusColor = ["red", "blue"];
        const statusName = ["LOAD OUT", "DROP EMPTY"];
        return <Tag color={statusColor[type]}>{statusName[type]}</Tag>;
      },
    },
    {
      title: "DATE SLOT",
      dataIndex: "dateSlot",
      key: "dateSlot",
      width: "120px",
      render: (_, record) => {
        // console.log(record["startTime"], record["endTime"]);
        const from = moment
          .utc(record["startTime"])
          .local()
          .format("MM-DD HH:mm");
        const to = moment.utc(record["endTime"]).local().format("MM-DD HH:mm");
        // console.log("from to", from, to);
        return (
          <Space direction="vertical">
            <a style={{ cursor: "default" }}>{from}</a>
            <a style={{ cursor: "default" }}>{to}</a>
          </Space>
        );
      },
    },
    {
      title: "STATUS #",
      key: "status",
      dataIndex: "status",
      render: (_, { id, status }) => {
        const statusColor = ["green", "gray", "red", "green"];
        const statusName = ["WORKING", "SUCCESS", "FAILED", "IN PROGRESS"];

        return (
          <Space size={1}>
            <Tag color={statusColor[status]}>{statusName[status]}</Tag>
            {status === 2 && (
              <Button
                type="link"
                style={{ color: "gray" }}
                onClick={(e) => {
                  e.preventDefault();
                  restartSave(id, 0);
                }}
              >
                Restart
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: "COUNTER",
      key: "counter",
      dataIndex: "counter",
      render: (counter) => {
        return <span>{counter}</span>;
      },
    },
    {
      title: "UPDATE TIME",
      dataIndex: "updateTime",
      width: "280px",
      key: "updateTime",
      render: (updateTime, { status }) => {
        const timeDifference = calculateTimeDifference(updateTime);
        const msg = `${timeDifference.minutes}分${timeDifference.seconds}秒`;
        const dateStr = moment
          .utc(updateTime)
          .local()
          .format("YYYY-MM-DD HH:mm:ss");
        return (
          <span>
            {dateStr}{" "}
            {(status === 0 || status === 3) && <span style={{ color: "red" }}>{msg}</span>}
          </span>
        );
      },
    },
    {
      title: "REMARK",
      key: "remark",
      dataIndex: "remark",
      width: "350px",
      render: (remark) => {
        return (
          <Tooltip title={remark}>
            <div
              style={{
                width: "350px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {remark}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (record) => {
        return (
          <Space size="middle">
            {record["status"] !== 1 && (
              <Popconfirm
                title="Are you sure?"
                onConfirm={() => handleDelte(record)}
              >
                <a style={{ color: "grey" }}>Delete</a>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card
        extra={
          <Button
            type="primary"
            style={{ width: "120px" }}
            onClick={(e) => {
              e.preventDefault();
              editRef.current = {};
              showModal();
            }}
          >
            Add
          </Button>
        }
        title={
          <>
            <Space>
              <Input
                style={{ width: "400px" }}
                onChange={(e) => {
                  //   console.log("e", e.target.value);
                  setContainer(e.target.value.trim());
                }}
              />
              <Button onClick={(e) => getListData(1)}>Search</Button>
            </Space>
          </>
        }
      >
        <Table
          bordered
          columns={columns}
          dataSource={dataList}
          pagination={{
            pageSize: sizeRef.current,
            total,
            onChange: getListData,
          }}
        />
      </Card>
      <Modal
        title="ADD APPOINTMENT"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose={true}
        footer={false}
        maskClosable={false}
      >
        <Form
          name="basic"
          labelCol={{
            span: 5,
          }}
          wrapperCol={{
            span: 18,
          }}
          initialValues={{ ...editRef.current }}
          onFinish={onFinish}
          autoComplete="off"
          style={{ paddingTop: "15px" }}
        >
          <Form.Item label="ID" name="id" style={{ display: "none" }}>
            <Input />
          </Form.Item>

          <Form.Item
            label="Terminal"
            name="terminal"
            rules={[
              {
                required: true,
                message: "Please select the terminal!",
              },
            ]}
          >
            <Select>
              <Select.Option value="lbct">LBCT</Select.Option>
              <Select.Option value="apm">APM</Select.Option>
              <Select.Option value="tti">TTI</Select.Option>
              <Select.Option value="pct">PCT</Select.Option>
              <Select.Option value="piera">Pier A</Select.Option>
              <Select.Option value="ets">ETS</Select.Option>
              <Select.Option value="wbct">WBCT</Select.Option>
              <Select.Option value="fms">APL/FMS</Select.Option>
              <Select.Option value="trapac">TRAPAC</Select.Option>
              {/* <Select.Option value="trapac">TRAPAC</Select.Option>
              <Select.Option value="yti">YTI</Select.Option> */}
              {/* <Select.Option value="piera">PIERA</Select.Option> */}
            </Select>
          </Form.Item>

          <Form.Item
            label="TYPE"
            name="type"
            rules={[
              {
                required: true,
                message: "Please select the type!",
              },
            ]}
          >
            <Select>
              <Select.Option value={0}>LOAD OUT</Select.Option>
              <Select.Option value={1}>DROP EMPTY</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="CONTAINER">
            <Form.Item
              name="container"
              rules={[
                {
                  required: true,
                  message: "Please input container number!",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="shippingLine"
              style={{
                display: "inline-block",
                width: "calc(30% - 8px)",
              }}
            >
              <Input placeholder="Line" />
            </Form.Item>
            <Form.Item
              name="pin"
              style={{
                display: "inline-block",
                width: "calc(30% - 8px)",
                margin: "0 8px",
              }}
            >
              <Input placeholder="Pin" />
            </Form.Item>
            <Form.Item
              name="isoNumber"
              style={{
                display: "inline-block",
                width: "calc(30% - 8px)",
                margin: "0 8px",
              }}
            >
              <Input placeholder="ISO" />
            </Form.Item>
          </Form.Item>

          <Form.Item
            label="TIME SLOT"
            name="timeSlot"
            rules={[
              {
                required: true,
                message: "Please select time slot!",
              },
            ]}
          >
            <RangePicker
              showTime={{
                hideDisabledOptions: true,
                minuteStep: 30,
                format: "HH:mm",
              }}
              format={"YYYY-MM-DD HH:mm"}
            />
          </Form.Item>
{/* 
          <Form.Item
            label="SCRAPER"
            name="scraperSelector"
            rules={[
              {
                required: false,
                message: "(Optional) Select Scraper",
              },
            ]}
          >
            <Select
              defaultValue={null}
            >
              <Select.Option value={0}>LEGACY</Select.Option>
              <Select.Option value={1}>NEW</Select.Option>
            </Select>
          </Form.Item> */}

          <Form.Item
            wrapperCol={{
              offset: 8,
              span: 12,
            }}
          >
            <Space style={{ paddingTop: "10px" }}>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button onClick={(e) => handleCancel()}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
